import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import useSessionIdentity, { getSessionIdentity } from '../hooks/useSessionIdentity';
import { getUser } from '../utils/func';
import { blockingRevision, loadBlockState, readBlockedUsers, readBlockState, subscribeBlocking } from './store';

export function useBlockState(id) {
  const identity = useSessionIdentity();
  useSyncExternalStore(subscribeBlocking, blockingRevision, blockingRevision);
  useEffect(() => {
    const refresh = () => { if (document.visibilityState !== 'hidden') loadBlockState(id); };
    refresh();
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); window.removeEventListener('pageshow', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [id, identity]);
  return readBlockState(id) || {};
}

export function useBlockedUsers(enabled, query = '', paused = false) {
  const identity = useSessionIdentity();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ key: '', items: [], count: 0, status: 'idle' });
  const generation = useRef(null);
  const key = JSON.stringify([identity, query, enabled, paused]);
  useEffect(() => {
    const change = () => setAttempt(value => value + 1);
    window.addEventListener('wiw:blocking-changed', change);
    return () => window.removeEventListener('wiw:blocking-changed', change);
  }, []);
  useEffect(() => {
    const run = { key, active: true, pending: false, controller: null, cursor: null, cursors: new Set() };
    generation.current = run;
    if (!enabled || !getUser()?.id || paused) {
      setState(previous => ({ key, identity, items: [], count: enabled && previous.identity === identity ? previous.count : 0, status: 'idle' }));
      return () => { run.active = false; };
    }
    const current = () => run.active && identity === getSessionIdentity();
    run.fetch = async (cursor = null) => {
      if (!current() || run.pending) return;
      run.pending = true;
      run.controller = new AbortController();
      setState(previous => ({ ...previous, key, identity, error: false, loadingMore: Boolean(cursor),
        count: previous.identity === identity ? previous.count : 0,
        items: cursor ? previous.items : [], status: cursor ? 'ready' : 'loading' }));
      try {
        const response = await readBlockedUsers({ query, cursor, signal: run.controller.signal });
        if (!current()) return;
        if (cursor) run.cursors.add(cursor);
        run.cursor = response.next_cursor && !run.cursors.has(response.next_cursor) ? response.next_cursor : null;
        setState(previous => ({ key, identity, count: response.blocked_count, status: 'ready', cursor: run.cursor,
          items: cursor ? [...previous.items, ...response.items.filter(item => !previous.items.some(existing => existing.id === item.id))] : response.items }));
      } catch {
        if (current()) setState(previous => ({ ...previous, loadingMore: false, status: cursor ? 'ready' : 'error', error: true }));
      } finally { if (current()) run.pending = false; }
    };
    let timer;
    if (query) timer = setTimeout(() => run.fetch(), 300); else run.fetch();
    return () => { run.active = false; clearTimeout(timer); run.controller?.abort(); };
  }, [key, identity, query, enabled, paused, attempt]);
  const loadMore = useCallback(() => {
    const run = generation.current;
    if (run?.key === key && run.cursor) return run.fetch(run.cursor);
  }, [key]);
  const visible = state.key === key ? state : { key, items: [], count: state.identity === identity ? state.count : 0, status: 'loading' };
  return { ...visible, hasMore: Boolean(visible.cursor), error: visible.status === 'error' || visible.error, loadMore,
    retry: () => setAttempt(value => value + 1) };
}
